import _ from 'underscore';
import React, { useContext, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { LegacyI18nContext } from '@bingads-webui-react/legacy-i18n-context';
import { Text, Link } from '@fluentui/react';
import { ErrorMessages } from '@bingads-webui-react/validation';
import { ValidationGroup } from '@bingads-webui-react/primitive-validation';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { HelpPopupInline } from '@bingads-webui-react/help-popup';

import { WebsiteInput } from './website-input';
import { WebsiteItem } from './website-item';
import { websiteSchema } from './validation-schema';
import { selectedText } from './consts';
import { getWebsiteAndSmartPagesStyles as getStyles, websiteAndSmartPagesStyles as styles } from './styles';
import { getIsWebsiteSocialPlatform } from './helpers';

export const WebsiteAndSmartPages = (props) => {
  const {
    website,
    setWebsite,
    existingWebsiteSelected,
    setExistingWebsiteSelected,
    setIsValid,
    websiteLabel,
    websiteHelpTopic,
    isWebsiteSocialPlatform,
    setIsWebsiteSocialPlatform,
    unsupportedDomainMessage,
    alwaysShowError,
    websiteOptionType,
    smartPageOptionType,
    smartPageErrors,
    smartPageLearnMoreLink,
    isShowIcon,
    selectedIcon,
    unselectedIcon,
    showWebsiteOptions,
    customError,
    onCustomValidate,
    isRebranding,
  } = props;
  const i18n = useContext(I18nContext);
  const legacyI18n = useContext(LegacyI18nContext);
  const classes = useDefaultStyles(getStyles);

  const onValidate = useCallback((e) => {
    setIsValid(e.isValid);
    setIsWebsiteSocialPlatform(e.isValid && getIsWebsiteSocialPlatform(_.get(e, ['data', 'website'])));
    onCustomValidate(_.get(e, ['data', 'website']));
  }, [setIsValid, setIsWebsiteSocialPlatform, onCustomValidate]);

  const onClickExistingWebsite
    = useCallback(() => setExistingWebsiteSelected(true), [setExistingWebsiteSelected]);
  const onClickSmartPages
    = useCallback(() => setExistingWebsiteSelected(false), [setExistingWebsiteSelected]);

  const optionText = useMemo(() => i18n.getString(selectedText[smartPageOptionType]), [i18n, smartPageOptionType]);

  return (
    <div className={classNames('website-and-smart-pages', classes.container)}>
      {showWebsiteOptions &&
        <div className={classNames(classes.websiteItemContainer)}>
          <WebsiteItem
            selected={existingWebsiteSelected}
            onClick={onClickExistingWebsite}
            type={websiteOptionType}
            isShowIcon={isShowIcon}
            selectedIcon={selectedIcon}
            unselectedIcon={unselectedIcon}
            isRebranding={isRebranding}
          />
          <WebsiteItem
            selected={!existingWebsiteSelected}
            onClick={onClickSmartPages}
            type={smartPageOptionType}
            isShowIcon={isShowIcon}
            selectedIcon={selectedIcon}
            unselectedIcon={unselectedIcon}
            isRebranding={isRebranding}
          />
        </div>
      }
      <div className={classNames(classes.selectedOptionContainer, 'selected-option-container')}>
        {
          existingWebsiteSelected ?
            <ValidationGroup
              validationProps={{
                schema: websiteSchema,
                i18n: legacyI18n,
                onValidate,
              }}
            >
              <Text variant="medium" block styles={styles.title}>
                {websiteLabel || i18n.getString(_TL_('Your business website you want customers to visit'))}
                {websiteHelpTopic && <HelpPopupInline
                  aria-label={i18n.getString(_TL_('Click to learn more about website'))}
                  topic={websiteHelpTopic}
                />}
              </Text>
              <WebsiteInput
                website={website}
                setWebsite={setWebsite}
                alwaysShowError={alwaysShowError}
                style={styles.websiteInput}
              />
              {isWebsiteSocialPlatform && <ErrorMessages messages={[unsupportedDomainMessage]} />}
              {customError && <ErrorMessages messages={[customError]} />}

            </ValidationGroup>
            :
            <Text variant="medium" className="no-website-text" block>
              {optionText}&nbsp;
              <Link href={smartPageLearnMoreLink} target="_blank" rel="noopener noreferrer" styles={styles.link}>
                {i18n.getString(_TL_('Learn more'))}
              </Link>
              <ErrorMessages
                messages={smartPageErrors}
                className={classNames('error', 'smart-page-errors', classes.smartPageErrors)}
              />
            </Text>
        }
      </div>
    </div>);
};

WebsiteAndSmartPages.propTypes = {
  website: PropTypes.string,
  setWebsite: PropTypes.func.isRequired,
  existingWebsiteSelected: PropTypes.bool.isRequired,
  setExistingWebsiteSelected: PropTypes.func.isRequired,
  setIsValid: PropTypes.func,
  websiteLabel: PropTypes.string,
  websiteHelpTopic: PropTypes.string,
  isWebsiteSocialPlatform: PropTypes.bool,
  setIsWebsiteSocialPlatform: PropTypes.func,
  unsupportedDomainMessage: PropTypes.string,
  alwaysShowError: PropTypes.bool,
  websiteOptionType: PropTypes.string.isRequired,
  smartPageOptionType: PropTypes.string.isRequired,
  smartPageErrors: PropTypes.arrayOf(PropTypes.string),
  smartPageLearnMoreLink: PropTypes.string.isRequired,
  isShowIcon: PropTypes.bool,
  selectedIcon: PropTypes.string,
  unselectedIcon: PropTypes.string,
  showWebsiteOptions: PropTypes.bool,
  customError: PropTypes.string,
  onCustomValidate: PropTypes.func,
  isRebranding: PropTypes.bool,
};

WebsiteAndSmartPages.defaultProps = {
  unsupportedDomainMessage: null,
  website: undefined,
  isWebsiteSocialPlatform: false,
  setIsWebsiteSocialPlatform: _.noop,
  setIsValid: _.noop,
  websiteLabel: undefined,
  websiteHelpTopic: undefined,
  alwaysShowError: false,
  smartPageErrors: [],
  isShowIcon: true,
  selectedIcon: null,
  unselectedIcon: null,
  showWebsiteOptions: true,
  customError: undefined,
  onCustomValidate: _.noop,
  isRebranding: false,
};
